export type ToolOnboardingStep = {
  eyebrow: string;
  title: string;
  summary: string;
  bullets: string[];
};

export type ToolOnboardingConfig = {
  path: string;
  label: string;
  intro: string;
  steps: ToolOnboardingStep[];
};

const toolOnboardingConfigs: Record<string, ToolOnboardingConfig> = {
  '/dashboard': {
    path: '/dashboard',
    label: 'Accueil',
    intro: 'Repérez le projet courant, l’étape utile et la prochaine action sans lire trop longtemps.',
    steps: [
      {
        eyebrow: 'Vue rapide',
        title: 'Comprendre où vous en êtes',
        summary: 'Le hub affiche le projet courant, les étapes déjà faites et ce qui reste à faire.',
        bullets: ['Voir le projet actif', 'Suivre l’avancement', 'Reprendre sans repartir de zéro'],
      },
      {
        eyebrow: 'Action',
        title: 'Aller à l’étape suivante',
        summary: 'Chaque écran propose une prochaine action claire pour garder le rythme.',
        bullets: ['Créer un projet', 'Continuer le cadrage', 'Revenir au bon endroit'],
      },
      {
        eyebrow: 'Rappel',
        title: 'Rouvrir le guide quand vous voulez',
        summary: 'Vous pouvez relancer cet onboarding depuis la barre du haut à tout moment.',
        bullets: ['Guide visible partout', 'Auto-affichage désactivable', 'Retour au travail en un clic'],
      },
    ],
  },
  '/tools/profile': {
    path: '/tools/profile',
    label: 'Profil',
    intro: 'Renseignez vos repères de travail pour préremplir le reste du Hub.',
    steps: [
      {
        eyebrow: 'Identité',
        title: 'Votre base de travail',
        summary: 'Nom, rôle, société et contact servent à personnaliser les outils.',
        bullets: ['Renseigner le profil', 'Garder un rendu propre', 'Préparer les partages'],
      },
      {
        eyebrow: 'Valeurs',
        title: 'Votre prix et votre rythme',
        summary: 'Le TJM et le nombre de jours par mois alimentent les calculs.',
        bullets: ['TJM par défaut', 'Jours par mois', 'Objectif net'],
      },
      {
        eyebrow: 'Usage',
        title: 'Tout le reste s’aligne',
        summary: 'Le profil sert de source de départ pour les missions, le calcul et la trésorerie.',
        bullets: ['Préremplissage', 'Moins de saisie', 'Plus de cohérence'],
      },
    ],
  },
  '/tools/eval': {
    path: '/tools/eval',
    label: 'Évaluer une opportunité',
    intro: 'Qualifiez une mission avant de vous engager — GO, NO-GO ou conditionnel.',
    steps: [
      {
        eyebrow: 'Décision',
        title: 'Évaluer vite',
        summary: 'Le questionnaire vous aide à décider si la mission vaut le coup.',
        bullets: ['Go / No go', 'Points de vigilance', 'Créer la mission si c’est utile'],
      },
      {
        eyebrow: 'Suite',
        title: 'Passer à la vue du projet',
        summary: 'Une fois l’évaluation faite, la suite logique est SIPOC puis MoSCoW.',
        bullets: ['Vue du projet', 'Priorités', 'Rôles'],
      },
    ],
  },
  '/tools/sipoc': {
    path: '/tools/sipoc',
    label: 'Vue du projet',
    intro: 'Décrivez le projet en clair avant de détailler les priorités et les rôles.',
    steps: [
      {
        eyebrow: 'Structure',
        title: 'Voir le projet en une page',
        summary: 'SIPOC aide à garder une vue simple: qui fournit quoi, à qui, et avec quelle logique.',
        bullets: ['Fournisseurs', 'Entrées', 'Sorties'],
      },
      {
        eyebrow: 'Clarté',
        title: 'Rester lisible',
        summary: 'L’objectif est de comprendre le projet sans jargon ni surcharge.',
        bullets: ['Peu de texte', 'Blocs courts', 'Lecture rapide'],
      },
      {
        eyebrow: 'Suite',
        title: 'Aller aux priorités',
        summary: 'Quand la vue est claire, vous pouvez décider ce qui compte le plus.',
        bullets: ['MoSCoW', 'RACI', 'Continuité du workflow'],
      },
    ],
  },
  '/tools/moscow': {
    path: '/tools/moscow',
    label: 'Priorites',
    intro: 'Classez ce qui compte le plus pour éviter les efforts dispersés.',
    steps: [
      {
        eyebrow: 'Tri',
        title: 'Séparer l’essentiel du reste',
        summary: 'MoSCoW sert à garder les besoins critiques visibles en premier.',
        bullets: ['Must', 'Should', 'Could', 'Won’t'],
      },
      {
        eyebrow: 'Arbitrage',
        title: 'Décider plus vite',
        summary: 'Le tri des priorités aide à avancer sans tout faire en même temps.',
        bullets: ['Moins d’hésitation', 'Plus de clarté', 'Plus d’impact'],
      },
      {
        eyebrow: 'Suite',
        title: 'Passer aux rôles',
        summary: 'Une fois les priorités posées, il faut savoir qui fait quoi.',
        bullets: ['Rôles', 'Qui fait quoi', 'Exécution'],
      },
    ],
  },
  '/tools/roles': {
    path: '/tools/roles',
    label: 'Rôles',
    intro: 'Cette page sert d’entrée courte avant d’ouvrir la matrice complète.',
    steps: [
      {
        eyebrow: 'Entrée',
        title: 'Comprendre les rôles',
        summary: 'Cette vue résume qui porte quoi avant d’ouvrir Qui fait quoi.',
        bullets: ['Vue courte', 'Repères simples', 'Lecture rapide'],
      },
      {
        eyebrow: 'Passage',
        title: 'Aller vers RACI',
        summary: 'Le détail des assignations se fait dans la matrice complète.',
        bullets: ['Responsable', 'Décideur', 'Consulté', 'Informé'],
      },
      {
        eyebrow: 'Suite',
        title: 'Passer au partage client',
        summary: 'Une fois les rôles clairs, on peut préparer la synthèse partageable.',
        bullets: ['RACI', 'Partage client', 'Continuité'],
      },
    ],
  },
  '/tools/raci': {
    path: '/tools/raci',
    label: 'Qui fait quoi',
    intro: 'Attribuez les responsabilités sans perdre la lisibilité de la matrice.',
    steps: [
      {
        eyebrow: 'Matrice',
        title: 'Distribuer les rôles',
        summary: 'RACI sert à savoir qui agit, qui décide, qui consulte et qui suit.',
        bullets: ['R', 'A', 'C', 'I'],
      },
      {
        eyebrow: 'Lecture',
        title: 'Garder une vue claire',
        summary: 'Le but est d’éviter les doublons et les zones grises.',
        bullets: ['Rôles visibles', 'Assignations propres', 'Moins de flou'],
      },
      {
        eyebrow: 'Suite',
        title: 'Ouvrir le partage client',
        summary: 'La prochaine étape est le bloc partageable pour le client.',
        bullets: ['Partage client', 'Partage', 'Lecture simple'],
      },
    ],
  },
  '/tools/deal-room': {
    path: '/tools/deal-room',
    label: 'Partage client',
    intro: 'Préparez un résumé partageable, lisible et facile à montrer.',
    steps: [
      {
        eyebrow: 'Partage',
        title: 'Synthèse courte',
        summary: 'Le partage client rassemble le contexte, l’objectif et les livrables.',
        bullets: ['Contexte', 'Objectif', 'Livrables'],
      },
      {
        eyebrow: 'Lecture',
        title: 'Partager sans bruit',
        summary: 'Le mode read-only sert à montrer l’essentiel sans exposer le reste.',
        bullets: ['Lien partagé', 'Lecture seule', 'Thème clair/sombre'],
      },
      {
        eyebrow: 'Suite',
        title: 'Passer au calendrier',
        summary: 'Après le résumé, on suit les jours et l’avancement réel.',
        bullets: ['Calendrier', 'Jours saisis', 'Suivi'],
      },
    ],
  },
  '/tools/cra': {
    path: '/tools/cra',
    label: 'Calendrier',
    intro: 'Suivez les jours du mois et transformez-les en revenu lisible.',
    steps: [
      {
        eyebrow: 'Temps',
        title: 'Voir le mois',
        summary: 'Le calendrier montre les jours travaillés, posés ou disponibles.',
        bullets: ['Jours saisis', 'Mois courant', 'Copier le mois précédent'],
      },
      {
        eyebrow: 'Revenu',
        title: 'Relier le temps au chiffre',
        summary: 'Le suivi des jours alimente le chiffre du mois et la trésorerie.',
        bullets: ['Jours', 'CA', 'Projection'],
      },
      {
        eyebrow: 'Suite',
        title: 'Aller à Argent',
        summary: 'Quand le calendrier est clair, on peut voir ce qui reste dans la caisse.',
        bullets: ['Argent', 'Entrées', 'Sorties'],
      },
    ],
  },
  '/tools/tresorerie': {
    path: '/tools/tresorerie',
    label: 'Argent',
    intro: 'Regardez ce qui entre, ce qui sort et ce qu’il reste vraiment.',
    steps: [
      {
        eyebrow: 'Base',
        title: 'Lire les flux',
        summary: 'La trésorerie montre les entrées, les sorties et le reste disponible.',
        bullets: ['Entrées', 'Sorties', 'Reste'],
      },
      {
        eyebrow: 'Décision',
        title: 'Voir le point de vigilance',
        summary: 'Les charges, le point mort et les scénarios aident à décider vite.',
        bullets: ['Charges fixes', 'Charges variables', 'Point mort'],
      },
      {
        eyebrow: 'Suite',
        title: 'Passer à la réserve',
        summary: 'Quand le mois est lisible, on projette la durée d’activité.',
        bullets: ['Reserve', 'Cas prudent', 'Cas actif'],
      },
    ],
  },
  '/tools/runway': {
    path: '/tools/runway',
    label: 'Reserve',
    intro: 'Projetez combien de temps vous tenez avec des scénarios simples.',
    steps: [
      {
        eyebrow: 'Projection',
        title: 'Lire la durée',
        summary: 'La réserve aide à voir le temps restant selon plusieurs cas.',
        bullets: ['Prudent', 'Normal', 'Actif'],
      },
      {
        eyebrow: 'Action',
        title: 'Savoir quoi faire',
        summary: 'Le but est de décider rapidement si vous devez agir ou continuer.',
        bullets: ['Réduire les coûts', 'Vendre plus', 'Allonger la piste'],
      },
      {
        eyebrow: 'Suite',
        title: 'Retourner au suivi',
        summary: 'La réserve se lit avec Argent et Calendrier pour garder le fil.',
        bullets: ['Argent', 'Calendrier', 'Suivi'],
      },
    ],
  },
  '/tools/calc-mission': {
    path: '/tools/calc-mission',
    label: 'Calc mission',
    intro: 'Choisissez un template, ajustez le calcul et gardez l’âme du moteur.',
    steps: [
      {
        eyebrow: 'Départ',
        title: 'Choisir une base',
        summary: 'Le catalogue vous donne un point de départ selon le métier ou le besoin.',
        bullets: ['Free', 'Starter', 'Pro'],
      },
      {
        eyebrow: 'Moteur',
        title: 'Calculer sans perdre le sens',
        summary: 'Le mode simple et le scratchpad gardent le calcul lisible et utile.',
        bullets: ['Prix jour', 'Jours', 'Marge', 'Net'],
      },
      {
        eyebrow: 'Suite',
        title: 'Préparer Missio ou le partage',
        summary: 'Le calcul sert ensuite à pousser un contexte vers Missio ou le lien partagé.',
        bullets: ['Templates', 'Session', 'Partage'],
      },
    ],
  },
  '/tools/missio': {
    path: '/tools/missio',
    label: 'Missio',
    intro: 'Pilotez vos missions, vos factures et votre progression au même endroit.',
    steps: [
      {
        eyebrow: 'Cockpit',
        title: 'Voir le pipeline',
        summary: 'Missio rassemble les missions à explorer, cadrer, livrer et facturer.',
        bullets: ['Pipeline', 'Missions', 'Factures'],
      },
      {
        eyebrow: 'Action',
        title: 'Modifier ou dupliquer',
        summary: 'Le cockpit permet d’éditer vite une mission ou de repartir d’une base existante.',
        bullets: ['Créer', 'Modifier', 'Dupliquer'],
      },
      {
        eyebrow: 'Suite',
        title: 'Ouvrir la version complète',
        summary: 'Le Hub prépare le contexte; le runtime expert prend le relais si besoin.',
        bullets: ['Facturation', 'Détail mission', 'Version complète'],
      },
    ],
  },
};

export function getToolOnboardingConfig(pathname: string): ToolOnboardingConfig | null {
  return toolOnboardingConfigs[pathname] ?? null;
}

export function getToolOnboardingPaths() {
  return Object.keys(toolOnboardingConfigs);
}

export function getToolOnboardingStorageKey(pathname: string) {
  return `indxone.onboarding.${pathname.replace(/^\//, '').replace(/\//g, '.').replace(/[^a-z0-9.-]/gi, '')}.auto`;
}
